/**
 * Quién puede entrar a /proyectos: quien tiene `projects.view` O quien fue
 * sumado al equipo de al menos un proyecto.
 *
 * Compartir un proyecto le da a esa persona, como mínimo, lectura: la RLS de
 * `core.projects` mira `project_members` desde la migración 20260926130000. Un
 * rol sin `projects.view` (el vendedor, un invitado) al que le compartieron un
 * proyecto tiene que poder abrirlo, y el guard de la ruta, el menú lateral, el
 * buscador ⌘K y el lanzador lo tapaban antes de que la RLS llegara a decidir.
 *
 * La membresía propia se lee de `project_members` con `user_id = yo`, que la
 * policy de SELECT permite siempre.
 */
import { useQuery } from '@tanstack/react-query';

import { usePermissions } from '@/hooks/usePermissions';
import { useSupabase } from '@/hooks/useSupabase';
import { supabase } from '@/lib/supabase';
import type { AppPermission } from '@/types/app-permissions';

// project_members no está hand-curated en database.types.ts · cast a any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export const MIS_MEMBRESIAS_KEY = ['core', 'project_members', 'mias'] as const;

async function tengoAlgunProyecto(userId: string): Promise<boolean> {
  const { data, error } = await db
    .from('project_members')
    .select('id')
    .eq('user_id', userId)
    .limit(1);
  if (error) throw error;
  return (data ?? []).length > 0;
}

export function useAccesoAProyectos(): { puede: boolean; cargando: boolean } {
  const { has } = usePermissions();
  const { user, loading } = useSupabase();
  const porPermiso = has('projects.view');

  const { data: porMembresia = false, isLoading } = useQuery({
    queryKey: [...MIS_MEMBRESIAS_KEY, user?.id ?? ''],
    queryFn: () => tengoAlgunProyecto(user!.id),
    enabled: !!user?.id && !porPermiso,
    staleTime: 5 * 60_000,
  });

  return {
    puede: porPermiso || porMembresia,
    cargando: loading || (!porPermiso && !!user?.id && isLoading),
  };
}

/**
 * El filtro de navegación (menú, ⌘K, lanzador) con la única excepción que no
 * sale de un permiso: la entrada a Proyectos también la abre la membresía.
 */
export function usePuedeVerEntrada(): (permission: AppPermission | null) => boolean {
  const { has } = usePermissions();
  const { puede } = useAccesoAProyectos();
  return (permission) => {
    if (permission === null) return true;
    if (permission === 'projects.view') return puede;
    return has(permission);
  };
}
